/**
 * useDriveFolders — TanStack Query wrapper around
 * ``listDriveFolders``. Backs the composer FolderPicker chip.
 *
 * Cache strategy:
 *   - ``staleTime: 5 minutes`` — folder selection rarely changes during a
 *     single chat session; the picker stays snappy without burning the BE.
 *   - Invalidation on connector add/remove (TODO once those mutations land)
 *     is fine to leave manual for now — the FE refetches naturally on
 *     window focus thanks to the QueryClient default.
 *
 * v1 source-of-truth: none — v1 hardcoded an empty folder list.
 */

import {
  type DriveFolder,
  type DriveFolderProvider,
  type ListDriveFoldersFilters,
  type ScopeFolderRow,
  type ScopeFolderSource,
  listDriveFolders,
  listScopeFolders,
} from "@ando/core/api";
import { type UseQueryResult, useQuery } from "@tanstack/react-query";

const STALE_TIME_MS = 5 * 60 * 1000;

export function driveFoldersQueryKey(filters?: ListDriveFoldersFilters): readonly unknown[] {
  return ["drive-folders", filters?.provider ?? null, filters?.limit ?? null] as const;
}

export function useDriveFolders(
  filters?: ListDriveFoldersFilters,
): UseQueryResult<readonly DriveFolder[], Error> {
  return useQuery({
    queryKey: driveFoldersQueryKey(filters),
    queryFn: () => listDriveFolders(filters),
    staleTime: STALE_TIME_MS,
  });
}

// ── Unified scope folders (June 8, 2026) ──────────────────────────────
//
// Backs the composer SourceScopePicker — aggregates Drive folders +
// filesystem-desktop base paths in a single query so the picker stays
// single-source. The ``filter_key`` field on each row is what we push
// to the WS ``folder_filter[]`` envelope.

export const scopeFoldersQueryKey = ["scope-folders"] as const;

export function useScopeFolders(): UseQueryResult<
  readonly ScopeFolderRow[],
  Error
> {
  return useQuery({
    queryKey: scopeFoldersQueryKey,
    queryFn: ({ signal }) => listScopeFolders(signal),
    staleTime: STALE_TIME_MS,
  });
}

export type {
  DriveFolder,
  DriveFolderProvider,
  ScopeFolderRow,
  ScopeFolderSource,
};
